import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";

interface ActionsMenuProps {
  onEdit?: () => void;
  onDelete?: () => void;
  onDeleteConfirm?: (proceed: () => void) => void; // if provided, will be called to confirm before delete
  editLabel?: string;
  deleteLabel?: string;
  disabledEdit?: boolean;
  disabledDelete?: boolean;
  align?: "start" | "end" | "center";
  extraItems?: Array<{ label: string; onClick: () => void; icon?: React.ReactNode; disabled?: boolean }>;
}

export default function ActionsMenu({
  onEdit,
  onDelete,
  onDeleteConfirm,
  editLabel = "Edit",
  deleteLabel = "Delete",
  disabledEdit,
  disabledDelete,
  align = "end",
  extraItems,
}: ActionsMenuProps) {
  // Allow Radix Dropdown to fully close before opening dialogs to avoid aria-hidden focus warnings
  const defer = (fn?: () => void) => {
    if (!fn) return;
    // Using a micro delay gives Radix time to clean up focus and aria-hidden states
    setTimeout(() => fn(), 0);
  };
  function handleDeleteClick() {
    const proceed = onDelete ?? (() => {});
    if (onDeleteConfirm) {
      defer(() => onDeleteConfirm(proceed));
    } else if (onDelete) {
      defer(onDelete);
    }
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0" aria-label="Open actions menu">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        {extraItems?.map((item, idx) => (
          <DropdownMenuItem
            key={idx}
            className="flex items-center"
            disabled={item.disabled}
            onClick={() => defer(item.onClick)}
          >
            {item.icon}
            <span className={item.icon ? "ml-2" : ""}>{item.label}</span>
          </DropdownMenuItem>
        ))}
        {extraItems && extraItems.length > 0 && (onEdit || onDelete) && <DropdownMenuSeparator />}
        {onEdit && (
          <DropdownMenuItem className="flex items-center" disabled={disabledEdit} onClick={() => defer(onEdit)}>
            <Pencil className="text-blue-600" />
            <span className="ml-2">{editLabel}</span>
          </DropdownMenuItem>
        )}
        {(onDelete || onDeleteConfirm) && (
          <DropdownMenuItem className="flex items-center" disabled={disabledDelete} onClick={handleDeleteClick}>
            <Trash2 color="red" />
            <span className="ml-2">{deleteLabel}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
